import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _every from 'lodash/every';
import SelectPanel from '@lib/components/SelectPanel/SelectPanel';
import { SectorOption, SiteOption } from '@lib/components/SelectPanel/options';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getOptionsArrayValidation } from '@lib/utils/validationUtils';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import generateIdsFieldKey from '@lib/utils/generateIdsFieldKey';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { materialSymbolsOutlined } from '@lib/enums/common';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import { FieldNames } from '../enums';
import styles from './getFilterFields.module.scss';

export default function useFilterFields({
  getSectorsLoadOptions,
  getSitesLoadOptions,
  getEquipmentLoadOptions,
  getCategoriesLoadOptions,
  clearFilter,
  isFavoritedMode,
  isFullAdmin,
  isViewLocked,
  onToggleFavoritedMode,
}: {
  getSectorsLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getEquipmentLoadOptions: GetLoadOptions;
  getCategoriesLoadOptions: GetLoadOptions;
  clearFilter: () => void;
  isFavoritedMode: boolean;
  isFullAdmin?: boolean;
  isViewLocked?: boolean;
  onToggleFavoritedMode: () => void;
}): FieldItem[] {
  const { t } = useTranslation();
  // Premium filter args (Favorites, Sectors, Sites, Categories) are gated by
  // `documentation_management` on the BE. Lock policy:
  //   • Full Admin on a plan-locked company → keep the chip + selects but
  //     render them disabled, so the user sees what's gated and is nudged
  //     to upgrade.
  //   • Non-Full-Admin on a plan-locked company → hide entirely (no upgrade
  //     CTA they can act on, and stale values can't trigger
  //     UPGRADE_PLAN_REQUIRED).
  const isPremiumDisabled = !!isViewLocked && !!isFullAdmin;
  const premiumDisabledTooltipProps = isPremiumDisabled
    ? { body: t('upgrade-plan-to-unlock') }
    : undefined;
  const fields: FieldItem[] = [
    {
      name: FieldNames.Favorites,
      label: '',
      customRender: () => (
        <Chip
          label={t('favorites')}
          leftIcon={isPremiumDisabled ? 'lock' : 'bookmark_star'}
          leftIconSymbolsOutlined
          leftIconFilled={!isPremiumDisabled}
          onClick={isPremiumDisabled ? undefined : onToggleFavoritedMode}
          rightIcon={
            !isPremiumDisabled && isFavoritedMode ? 'check_circled' : undefined
          }
          rightIconClassName={styles.blueIcon}
          size={ChipSizes.large}
          type={
            isPremiumDisabled ? ChipTypes.chipDisabled : ChipTypes.chipFilter
          }
          tooltip={isPremiumDisabled ? t('upgrade-plan-to-unlock') : undefined}
        />
      ),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isOptionalFn: () => true,
    },
    {
      name: FieldNames.Sectors,
      label: t('sector'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      componentProps: {
        defaultOptions: false,
        disabled: isPremiumDisabled,
        disabledTooltipProps: premiumDisabledTooltipProps,
        getLoadOptions: getSectorsLoadOptions,
        renderOption: SectorOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'share_location',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === FieldNames.Sectors) {
          setValue(FieldNames.Sites, [], {
            shouldValidate: false,
          });
        }
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Sites,
      label: t('site'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      componentProps: {
        defaultOptions: false,
        disabled: isPremiumDisabled,
        disabledTooltipProps: premiumDisabledTooltipProps,
        getLoadOptions: getSitesLoadOptions,
        renderOption: SiteOption,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'home_pin',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      getFieldKeyFn: (values: Values) => {
        const rawSectorIds = _get(values, FieldNames.Sectors, []) as Array<
          { value?: string } | string
        >;
        return generateIdsFieldKey(rawSectorIds, {
          prefix: `${FieldNames.Sectors}-sites`,
          emptyKey: `${FieldNames.Sectors}-no-sites`,
        });
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Equipment,
      label: t('asset'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      componentProps: {
        defaultOptions: false,
        getLoadOptions: getEquipmentLoadOptions,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'inventory',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Categories,
      label: t('category'),
      element: SelectPanel,
      validation: getOptionsArrayValidation({ t }),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      componentProps: {
        defaultOptions: false,
        disabled: isPremiumDisabled,
        disabledTooltipProps: premiumDisabledTooltipProps,
        getLoadOptions: getCategoriesLoadOptions,
        isAsync: true,
        isMulti: true,
        showTabs: true,
        buttonProps: {
          leftIcon: 'category',
          leftIconClassName: materialSymbolsOutlined,
        },
      },
      isOptionalFn: () => false,
    },
    {
      name: FieldNames.Reset,
      label: '',
      customRender: () => (
        <Button
          buttonSize={ButtonSizes.large}
          buttonText={t('reset')}
          buttonType={ButtonTypes.primaryText}
          onClick={clearFilter}
        />
      ),
      formItemClassName: formStyles.formColumnAuto,
      hideErrorsSpace: true,
      isOptionalFn: () => true,
      isVisibleFn: (values: Values) => {
        const sectors = _get(values, FieldNames.Sectors, []);
        const sites = _get(values, FieldNames.Sites, []);
        const equipment = _get(values, FieldNames.Equipment, []);
        const categories = _get(values, FieldNames.Categories, []);
        return (
          isFavoritedMode ||
          !_every([sectors, sites, equipment, categories], _isEmpty)
        );
      },
    },
  ];
  if (isViewLocked && !isFullAdmin) {
    // Non-Full-Admin users on a plan-locked company can't upgrade and have
    // no use for the premium filters — drop the toggle chip + premium
    // filter fields entirely. Equipment + Reset stay. Full Admin gets the
    // disabled-but-visible treatment handled above via `isPremiumDisabled`.
    return fields.filter(
      ({ name }) =>
        name !== FieldNames.Favorites &&
        name !== FieldNames.Sectors &&
        name !== FieldNames.Sites &&
        name !== FieldNames.Categories,
    );
  }
  return fields;
}

export { FieldNames } from '../enums';
